<template>
  <CascadeSelect
    v-if="cascade"
    :model-value="internalModelValue"
    :data-cy="name && `${kebabCase(name)}-dropdown`"
    :pt="pt"
    v-bind="$attrs"
    @update:model-value="update"
  >
    <!-- @vue-skip s as string -->
    <template v-for="(_, s) in $slots" #[s]="slotProps">
      <slot :name="s" v-bind="slotProps"></slot>
    </template>
  </CascadeSelect>
  <MultiSelect
    v-else-if="multiple"
    :model-value="internalModelValue"
    :data-cy="name && `${kebabCase(name)}-dropdown`"
    placeholder="Select option(s)"
    v-bind="$attrs"
    :pt="{
      closeButton: { class: 'hidden' },
      ...pt,
    }"
    :show-toggle-all="showMultiselectToggleAll"
    @update:model-value="update"
  >
    <!-- @vue-skip s as string -->
    <template v-for="(_, s) in $slots" #[s]="slotProps">
      <slot :name="s" v-bind="slotProps"></slot>
    </template>
    <template #header>
      <!-- Spacing and font override classes throughout this header help
       our group select checkbox(es) fit the style of the built-in "All" select -->
      <div
        v-if="groupSelectOptions.length"
        class="flex flex-col gap-4 px-4 py-2"
        @click.stop
      >
        <div v-for="group in groupSelectOptions" :key="group.label">
          <TCheckbox
            :model-value="
              isGroupSelected(group, internalModelValue) ? [true] : []
            "
            :value="true"
            :input-id="`group-${group.label.replaceAll(' ', '-')}`"
            :data-cy="`grouped-select-checkbox-group-${group.label.replaceAll(
              ' ',
              '-'
            )}`"
            @update:model-value="
              (newVal) =>
                update(
                  toggleGroupSelection(
                    group,
                    internalModelValue ?? [],
                    newVal.length > 0
                  )
                )
            "
          />
          <label
            :for="`group-${group.label.replaceAll(' ', '-')}`"
            class="ml-3 font-normal text-nash-neutral800 cursor-pointer"
            >{{ group.label }}</label
          >
        </div>
      </div>
    </template>
  </MultiSelect>
  <PVSelect
    v-else
    :model-value="internalModelValue"
    :data-cy="name && `${kebabCase(name)}-dropdown`"
    :pt="pt"
    show-clear
    v-bind="$attrs"
    option-disabled="optionDisabled"
    @update:model-value="update"
  >
    <!-- @vue-skip s as string -->
    <template v-for="(_, s) in $slots" #[s]="slotProps">
      <slot :name="s" v-bind="slotProps"></slot>
    </template>
  </PVSelect>
</template>

<script lang="ts">
import kebabCase from 'lodash-es/kebabCase'
import CascadeSelect from 'primevue/cascadeselect'
import MultiSelect from 'primevue/multiselect'
import Select from 'primevue/select'
import { useField } from 'vee-validate'
import { defineComponent, ref, watch } from 'vue'
import TCheckbox from '../checkbox/TCheckbox.vue'
import {
  GroupSelectOption,
  isGroupSelected,
  toggleGroupSelection,
} from '../groupSelectUtils.js'
import type { PropType } from 'vue'

export default defineComponent({
  components: {
    CascadeSelect,
    PVSelect: Select,
    MultiSelect,
    TCheckbox,
  },
  props: {
    name: {
      type: String,
      default: null,
    },
    initialValue: {
      type: [String, Number, Array, Object, Boolean] as PropType<any>,
      default: null,
    },
    // adds compatibility with v-model
    modelValue: {
      type: [String, Number, Array, Object, Boolean] as PropType<any>,
      default: null,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    pt: {
      type: Object,
      default: () => ({}),
    },
    cascade: {
      type: Boolean,
      default: false,
    },
    rules: { type: String, default: '' },
    showMultiselectToggleAll: {
      type: Boolean,
      default: true,
    },
    groupSelectOptions: {
      type: Array as PropType<GroupSelectOption[]>,
      default: () => [],
    },
  },
  emits: ['update:modelValue', 'change'],
  setup(props, context) {
    if (props.multiple && props.cascade) {
      console.error(
        'cannot set multiple and cascade at the same time on TDropdown'
      )
    }

    const internalModelValue = ref<any>(props.initialValue ?? props.modelValue)
    if (props.name && props.rules) {
      useField(props.name, props.rules, {
        initialValue: internalModelValue,
      })
    }
    watch(
      () => props.initialValue,
      (initialValue: any) => (internalModelValue.value = initialValue)
    )
    watch(
      () => props.modelValue,
      (modelValue: any) => (internalModelValue.value = modelValue)
    )
    const update = (newValue: any) => {
      internalModelValue.value = newValue

      context.emit('update:modelValue', newValue)
      context.emit('change', { value: newValue })
    }
    return {
      internalModelValue,
      kebabCase,
      update,
      isGroupSelected,
      toggleGroupSelection,
    }
  },
})
</script>

<style lang="scss">
$components: ('p-select', 'p-multiselect', 'p-cascadeselect');

@each $component in $components {
  // don't show label for icon dropdowns
  .#{$component}.p-icon-dropdown .#{$component}-label,
  .#{$component}.p-icon-dropdown .#{$component}-label-empty {
    padding: 0;
    list-style: none;
    display: none;
  }

  .#{$component}.p-icon-dropdown .#{$component}-dropdown {
    width: 1.5rem;
  }

  .#{$component}.p-icon-dropdown {
    @apply border-0;
  }
}
</style>
